import Markdown from "react-markdown"
import styled from "styled-components"

import { spacing, Text } from "@minuthq/meatball-ui-react"

import { usePostParadiseLLMPromptVersion } from "src/data/paradise/paradiseLLMPrompts/queries/paradiseLLMPromptsQueries"
import { TParadiseLLMPromptVersion } from "src/data/paradise/paradiseLLMPrompts/types/paradiseLLMPromptsQueryTypes"
import { MDialog } from "src/ui/Dialog/MDialog"
import { MoreButton } from "src/ui/GridTable/MoreButton"
import { MCard } from "src/ui/MCard/MCard"

export function ParadiseLLMPromptVersionDialog({
  onClose,
  version,
  promptId,
}: {
  onClose: () => void
  version: TParadiseLLMPromptVersion
  promptId: string
}) {
  const postLLMPromptVersion = usePostParadiseLLMPromptVersion()

  /**
   * There is not endpoint for reverting to a version,
   * so we need to create a new version with the same description and prompt.
   */
  function rollbackVersion() {
    postLLMPromptVersion.mutate(
      {
        path: {
          prompt_id: promptId,
        },
        body: {
          description: version.description,
          prompt: version.prompt,
        },
      },
      {
        onSuccess: () => {
          onClose()
        },
      }
    )
  }

  return (
    <MDialog
      open={true}
      onClose={onClose}
      title={
        <TitleWrapper>
          <Text variant="heading2">Prompt version</Text>
          <ButtonsWrapper>
            <MoreButton
              menuItems={[
                {
                  key: "rollback",
                  contents: <Text color="emergency">Rollback</Text>,
                  onClick: rollbackVersion,
                },
              ]}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              buttonVariant="subtle"
              buttonSize="small"
            />
          </ButtonsWrapper>
        </TitleWrapper>
      }
      width="large"
    >
      <ContentWrapper>
        <MCard border={true} boxShadow={false}>
          <Text variant="heading3" marginBottom={spacing.XS}>
            Description
          </Text>

          <StyledMarkdown>
            <Markdown>{version.description}</Markdown>
          </StyledMarkdown>
        </MCard>

        <MCard border={true} boxShadow={false}>
          <Text variant="heading3" marginBottom={spacing.XS}>
            Prompt
          </Text>

          <StyledMarkdown>
            <Markdown>{version.prompt}</Markdown>
          </StyledMarkdown>
        </MCard>
      </ContentWrapper>
    </MDialog>
  )
}

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const ButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.XS};
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.M};
  min-height: 60vh;
`

const StyledMarkdown = styled.div`
  font-family: monospace;
  font-size: 0.875rem;

  & p:first-child {
    margin-top: 0;
  }

  & p:last-child {
    margin-bottom: 0;
  }
`
